import { useEffect, useState } from 'react';

import { AccessTime, Fingerprint, ImageOutlined, ListAlt, TextSnippet, ToggleOn } from '@mui/icons-material';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';

import { IconComponent, ParameterTypes } from '@@common';
import AgentIcon from '../../../common/components/AgentIcon';

import { ParameterMetadata, ParameterType, PrimitiveType } from '../../../api/api';
import { IconExpression, IconIntegrationEvent, IconOperation, IconSchema, IconServiceConnection } from '../Components/Icons';

export type PropertyIconProps = {
	parameterMetadata: ParameterMetadata;
	isLoading?: boolean;
};

export default function PropertyIcon({ parameterMetadata, isLoading = false }: PropertyIconProps) {
	const [showLoading, setShowLoading] = useState(isLoading);
	useEffect(() => {
		if (isLoading) {
			setShowLoading(true);
			return;
		}

		const timeoutId = window.setTimeout(() => setShowLoading(false), 350);
		return () => window.clearTimeout(timeoutId);
	}, [isLoading]);

	if (showLoading) {
		return (
			<Box
				aria-label="Calculating variable scope"
				role="status"
				sx={{
					display: 'inline-flex',
					animation: 'property-icon-appear 180ms ease-out',
					'@keyframes property-icon-appear': {
						from: { opacity: 0, transform: 'scale(0.65)' },
						to: { opacity: 1, transform: 'scale(1)' },
					},
				}}
			>
				<CircularProgress size={18} thickness={5} />
			</Box>
		);
	}

	if (parameterMetadata.iconUrl || parameterMetadata.iconName) {
		return <IconComponent iconUrl={parameterMetadata.iconUrl} iconName={parameterMetadata.iconName} />;
	}

	if (parameterMetadata.typeCustom === ParameterTypes.AgentId) {
		return <AgentIcon />;
	}
	if (parameterMetadata.typeCustom === ParameterTypes.FileId) {
		return <ImageOutlined />;
	}
	if (parameterMetadata.typeCustom === ParameterTypes.StringMap) {
		return <ListAlt />;
	}
	if (parameterMetadata.type === ParameterType.Expression) {
		return <IconExpression />;
	}
	if (parameterMetadata.type === ParameterType.SchemaRef || parameterMetadata.type === ParameterType.SchemaValue) {
		return <IconSchema />;
	}
	if (parameterMetadata.type === ParameterType.OperationRef) {
		return <IconOperation />;
	}
	if (parameterMetadata.type === ParameterType.ServiceConnectionRef) {
		return <IconServiceConnection />;
	}
	if (parameterMetadata.type === ParameterType.IntegrationEventRef) {
		return <IconIntegrationEvent />;
	}
	if (parameterMetadata.type === ParameterType.Primitive && (parameterMetadata.valueOptions?.length || parameterMetadata.valueOptionsSource)) {
		return <ListAlt />;
	}

		switch (parameterMetadata.expectedType?.type) {
		case PrimitiveType.Object:
			return <IconSchema />;
		case PrimitiveType.Boolean:
			return <ToggleOn />;
		case PrimitiveType.Guid:
			return <Fingerprint />;
		case PrimitiveType.TimeSpan:
			return <AccessTime />;
		case PrimitiveType.String:
		case PrimitiveType.Decimal:
			return <TextSnippet />;
		default:
			return <TextSnippet />;
	}
}
